import userData from "@/services/clientUserData";
import { flatMapDeep } from 'lodash';
import AccordionLabelWithBadge from '@/components/SchoolSearch/Filters/RosterNeedsFilters/AccordionLabelWithBadge';
import AddGradYearAndPositionCTA from '@/components/SchoolSearch/Filters/RosterNeedsFilters/AddGradYearAndPositionCTA';


class NeedsFiltersResolver {
    constructor(needs, allPositions)  {
        this.needs = needs;
        this.allPositions = allPositions;
    }

    mapFullNeeds() {
        let fullMatchNeeds = {
            label: 'Full Match',
            value: 'full',
            depth: 1,
            labelComponent: AccordionLabelWithBadge,
            emptyStateComponent: AddGradYearAndPositionCTA,
            needType: 'full',
            persistTrigger: true,
            disableCheck: this.isFreeOrExpiredAthlete(),
            description: "Your profile contains and matches all of the information a college coach has added to a Roster Need.",
        }

        fullMatchNeeds.children = this.mapFullOrPotentialNeedsChildren(this.needs.full, 'full');
        fullMatchNeeds.disabled = !fullMatchNeeds.children.length;
        fullMatchNeeds.needsCount = this.computeNeedsCount(this.needs.full);

        return fullMatchNeeds;
    }

    mapPotentialNeeds() {
        let potentialMatchNeeds = {
            label: 'Potential Match',
            value: 'potential',
            depth: 1,
            needType: 'potential',
            labelComponent: AccordionLabelWithBadge,
            emptyStateComponent: AddGradYearAndPositionCTA,
            persistTrigger: true,
            disableCheck: this.isFreeOrExpiredAthlete(),
            description: "Your profile is missing some of the information or video a college coach has required to match a Roster Need. Add video and complete your profile to see if you become a Full Match."
        }

        potentialMatchNeeds.children = this.mapFullOrPotentialNeedsChildren(this.needs.potential, 'potential');
        potentialMatchNeeds.disabled = !potentialMatchNeeds.children.length;
        potentialMatchNeeds.needsCount = this.computeNeedsCount(this.needs.potential);

        return potentialMatchNeeds;
    }

    mapFullOrPotentialNeedsChildren(needs, type) {
        return Object.keys(needs).reduce((children, year) => {
            let chldrn = Object.keys(needs[year]).sort().map(position => ({
                label: `${year} ${position}`,
                value: `${type}.${year}.${position}`,
                depth: 2,
                needType: type,
                needsCount: this.computeNeedsCount(needs[year][position]),
                labelComponent: AccordionLabelWithBadge,
                disableCheck: this.isFreeOrExpiredAthlete()
            }));

            children.push(...chldrn);

            return children;
        },[])
    }

    mapAllRosterNeeds() {
        let allRosterNeeds = {
            label: 'All Roster Needs', 
            value: 'all',
            depth: 1,
            children: [],
            needType: 'all',
            needsCount: this.computeNeedsCount(this.needs),
            labelComponent: AccordionLabelWithBadge,
            disableCheck: this.isFreeOrExpiredAthlete()
        };

        if(userData.isPlayer) 
            this.addAllRosterNeedsChildrenForAthlete(allRosterNeeds);

        if(userData.isClubStaff) {
            this.addAllRosterNeedsChildrenForStaff(allRosterNeeds);
            allRosterNeeds.isOpen = true;
        }

        allRosterNeeds.disabled = !allRosterNeeds.children.length;

        return allRosterNeeds;
    }

    addAllRosterNeedsChildrenForAthlete(allRosterNeeds) {
        const flattenGroups = Object.values(this.needs);
        const yearAndPositionMap = flattenGroups.reduce((agg, group) => {
            for(const year in group) {
                agg[year] = (agg[year] === undefined) ? new Set([...Object.keys(this.allPositions)]) : new Set([...agg[year], ...Object.keys(this.allPositions)]);
            }

            return agg;
        }, {})

        allRosterNeeds.children = Object.keys(yearAndPositionMap).map(year => ({
            label: `${year}`,
            value: `${year}`,
            depth: 2,
            needType: 'all',
            needsCount: this.computeNeedsCount(flatMapDeep(this.needs).map(group => group[year]).filter(g => g)),
            labelComponent: AccordionLabelWithBadge,
            disableCheck: this.isFreeOrExpiredAthlete()
        }));

        allRosterNeeds.children.forEach(allRosterNeedsChild => {
            allRosterNeedsChild.children = [...yearAndPositionMap[allRosterNeedsChild.value]].sort().map(position => ({
                label: `${position}`,
                value: `non-match.${allRosterNeedsChild.value}.${position}`,
                depth: 3,
                needType: 'all',
                needsCount: flattenGroups.reduce((sum, group) => {
                    if(group[allRosterNeedsChild.value] === undefined || group[allRosterNeedsChild.value][position] === undefined) return sum;
                    return sum + group[allRosterNeedsChild.value][position].length;
                }, 0),
                labelComponent: AccordionLabelWithBadge,
                disableCheck: this.isFreeOrExpiredAthlete()
            }))
        });
    }

    addAllRosterNeedsChildrenForStaff(allRosterNeeds) {
        allRosterNeeds.children = Object.keys(this.needs).map(year => ({
            label: `${year}`,
            value: `${year}`,
            depth: 2,
            needType: 'all',
            needsCount: this.computeNeedsCount(this.needs[year]),
            labelComponent: AccordionLabelWithBadge
        }));

        allRosterNeeds.children.forEach(allRosterNeedsChild => {
            allRosterNeedsChild.children = Object.keys(this.needs[allRosterNeedsChild.value])
            .sort()
            .map(position => ({
                label: `${position}`,
                value: `${allRosterNeedsChild.value}.${position}`,
                depth: 3,
                needType: 'all',
                needsCount: this.computeNeedsCount(this.needs[allRosterNeedsChild.value][position]),
                labelComponent: AccordionLabelWithBadge

            }));
        });
    }

    computeNeedsCount(data) {
        if(Array.isArray(data) && data.length && typeof data[0] === "number")
            return data.length;

        if(Array.isArray(data) && data.length && typeof data[0] === "object")
            return data.reduce((sum, item) => sum + this.computeNeedsCount(item), 0)

        if(!Array.isArray(data) && typeof data === "object")
            return Object.keys(data).reduce((sum, item) => sum + this.computeNeedsCount(data[item]), 0);
        
        return 0;
    }

    isFreeOrExpiredAthlete() {
        return (userData.isFreeUser || userData.isExpiredUser) && !userData.isClubStaff;
    }
}

export default NeedsFiltersResolver;
